import { useMutation, useQuery } from "@apollo/client";
import type { IUseModal } from "@fluidattacks/design";
import isNil from "lodash/isNil";
import sortBy from "lodash/sortBy";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { GET_FINDINGS_POLICIES } from "../../../queries";
import { ADD_ORGANIZATION_FINDING_POLICY, GET_CRITERIA } from "../queries";
import type {
  IAddVulnerabilityTypeForm,
  IVulnerabilityCriteriaData,
} from "../types";
import type { TreatmentAcceptancePolicy } from "gql/graphql";
import { getFindingSuggestions } from "pages/group/findings/utils";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";

interface IUsePolicies {
  handleAddPolicy: (values: IAddVulnerabilityTypeForm) => Promise<void>;
  loading: boolean;
  suggestions: string[];
}

const tPath = "organization.tabs.policies.findings.";

const useAddVulnerabilityPolicy = (
  modal: IUseModal,
  organizationId: string,
  vulnerabilityTreatment: string,
): IUsePolicies => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const { t } = useTranslation();
  const { organizationName } = useParams() as { organizationName: string };
  const isAccepted = vulnerabilityTreatment === "ACCEPTED";

  useQuery(GET_CRITERIA, {
    fetchPolicy: "cache-and-network",
    onCompleted: (result): void => {
      const { edges } = result.criteriaConnection;
      const formattedVulns = Object.fromEntries(
        edges.map((vuln): [string, IVulnerabilityCriteriaData] => [
          vuln.node.findingNumber,
          vuln.node as IVulnerabilityCriteriaData,
        ]),
      );
      const findingSuggestions = getFindingSuggestions(formattedVulns);
      const currentSuggestions = sortBy(
        findingSuggestions.map((suggestion): string => suggestion.title),
      );
      setSuggestions(
        isAccepted
          ? ["All vulnerability types", ...currentSuggestions]
          : currentSuggestions,
      );
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load criteria vulnerabilities", error);
      });
    },
    skip: !modal.isOpen,
    variables: { after: "" },
  });

  const [addOrgFindingPolicy, { loading }] = useMutation(
    ADD_ORGANIZATION_FINDING_POLICY,
    {
      onCompleted: (result: {
        addOrganizationFindingPolicy: { success: boolean };
      }): void => {
        if (result.addOrganizationFindingPolicy.success) {
          msgSuccess(
            t(`${tPath}addPolicies.success`),
            t("sidebar.newOrganization.modal.successTitle"),
          );
        }
      },
      onError: (error): void => {
        error.graphQLErrors.forEach(({ message }): void => {
          switch (message) {
            case "Exception - The finding name is invalid":
              msgError(t("validations.draftTitle"));
              break;
            case "Exception - The finding name policy already exists":
              msgError(t(`${tPath}errors.duplicateFinding`));
              break;
            default:
              msgError(t("groupAlerts.errorTextsad"));
              Logger.error("Error adding finding policy", message);
          }
        });
      },
      refetchQueries: [
        { query: GET_FINDINGS_POLICIES, variables: { organizationId } },
      ],
    },
  );

  const addPolicy = useCallback(
    async (tags: string, type: string): Promise<void> => {
      await addOrgFindingPolicy({
        variables: {
          name: type,
          organizationName,
          tags: tags === "" ? undefined : tags.split(","),
          treatmentAcceptance:
            vulnerabilityTreatment as TreatmentAcceptancePolicy,
        },
      });
    },
    [addOrgFindingPolicy, organizationName, vulnerabilityTreatment],
  );
  const handleAddPolicy = useCallback(
    async (values: IAddVulnerabilityTypeForm): Promise<void> => {
      if (values.type !== "") await addPolicy(values.tags, values.type);
      else if (!isNil(values.checkAll) && values.checkAll !== "")
        await addPolicy(values.tags, values.checkAll);
      else if (!isNil(values.typeOptions) && values.typeOptions.length > 0)
        await Promise.all(
          values.typeOptions.map(async (type): Promise<void> => {
            await addPolicy(values.tags, type);
          }),
        );
      modal.close();
    },
    [addPolicy, modal],
  );

  return { handleAddPolicy, loading, suggestions };
};

export { useAddVulnerabilityPolicy };
